import { delay, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { call, select, take } from 'typed-redux-saga'

import { MEMBER_PROFILE_URL, ROOT_URL } from 'constants/routes'
import { Screen } from 'constants/tracking/screens'
import { ItemAfterUploadActions, PromotionStorageKeys } from 'constants/item-upload'
import * as api from 'data/api'
import { ResponseCode } from 'data/api/response-codes'
import {
  transformItemEditDto,
  transformSizeGroupDtos,
  transformDynamicAttributes,
} from 'data/transformers'
import { navigateToPage, scrollToElementById } from '_libs/utils/window'
import { tracker } from '_libs/common/tracker'
import {
  itemUploadStartEventExtra,
  itemUploadSubmitFail,
  itemUploadSubmitSuccess,
} from '_libs/common/event-tracker/events'

import { actions as packageSizesActions } from 'state/package-sizes/slice'
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors'
import {
  CompleteItemArgs,
  CreateItemArgs,
  CreateItemDraftArgs,
  ItemEditResp,
  ResponseError,
  SubmitDraftResp,
  SubmitItemResp,
  UpdateItemArgs,
  UpdateItemDraftArgs,
} from 'types/api'
import { GtmTrackableItemDto } from 'types/dtos'
import { UiState as UiStateEnum } from 'constants/ui'
import { getFieldErrors } from 'pages/ItemUpload/utils/errors'

import * as statelessActions from './actions'
import * as transformers from './transformers'
import * as selectors from './selectors'
import {
  REQUEST_DELAY,
  WITHOUT_BRAND_ID,
  FieldName,
  IsbnValidity,
  ItemUploadFailReason,
  ItemStatus,
} from './constants'
import { actions } from './slice'
import { setItemIdToStorage, setPromotionsAfterItemUploadToStorage } from './helpers'

export function* fetchCategoryAttributes(
  catalogId: number | null,
  itemStatus: ItemStatus,
  removeFieldError: (fieldName: FieldName) => void,
) {
  if (!catalogId) return

  yield put(actions.setDynamicAttributesUiState({ uiState: UiStateEnum.Pending }))

  const response = yield* call(api.getCategoryAttributes, catalogId)

  if ('errors' in response) {
    yield put(actions.setCatalogId({ id: null, itemStatus, removeFieldError }))
    yield put(actions.fetchCategoryAttributesFailure())

    return
  }

  yield put(
    actions.fetchCategoryAttributesSuccess({
      attributes: transformDynamicAttributes(response.attributes),
      additionalAttributes: response.additional_attributes,
    }),
  )
}

export function* fetchItem({
  payload: {
    id,
    tempUuid,
    userId,
    setItemStatus,
    removeFieldError,
    isMultipleSizeGroupsABTestEnabled,
  },
}: ReturnType<typeof statelessActions.fetchItemRequest>) {
  const isNewCreateAndEditItemEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_create_and_edit_item_new_web'),
  )

  yield put(statelessActions.fetchCatalogs())
  yield put(statelessActions.getDropOffLocationPrompt({ userId }))

  if (!id) {
    const sessionResponse = yield* call(api.getSessionDefaults)
    if ('errors' in sessionResponse) return

    yield put(
      actions.setCurrency({ currency: sessionResponse.session_defaults_configuration.currency }),
    )

    yield put(actions.setUiState({ uiState: UiStateEnum.Success }))
    yield call(
      tracker.track,
      itemUploadStartEventExtra({ tempUuid, screenName: Screen.ItemUpload }),
    )

    return
  }

  let additionalHeaders = {}
  if (isMultipleSizeGroupsABTestEnabled) {
    additionalHeaders = { 'X-Enable-Multiple-Size-Groups': 'true' }
  }

  let response: ItemEditResp | ResponseError<unknown>

  if (isNewCreateAndEditItemEnabled) {
    response = yield* call(api.getItemEdit, { id, additionalHeaders })
  } else {
    response = yield* call(api.getItemEditDeprecated, { id, additionalHeaders })
  }

  if ('errors' in response) {
    yield put(actions.setUiState({ uiState: UiStateEnum.Failure }))

    return
  }

  const item = transformItemEditDto(response.item)

  yield put(actions.setInitialItemData({ item }))

  const itemStatus = item.isDraft ? ItemStatus.DraftEdit : ItemStatus.Edit
  yield call(setItemStatus, itemStatus)

  yield put(actions.setCurrency({ currency: item.currency }))

  yield call(fetchCategoryAttributes, item.catalogId, itemStatus, removeFieldError)

  const selectedValues = transformers.transformDynamicAttributes(item.itemAttributes)
  yield put(actions.setSelectedDynamicAttributeValues({ selectedValues }))

  yield put(statelessActions.fetchPriceSuggestions())

  const isNewVerificationServiceEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_verification_service_web'),
  )
  if (!isNewVerificationServiceEnabled) {
    yield put(statelessActions.fetchItemOfflineVerificationEligibility())
  }

  yield put(actions.setUiState({ uiState: UiStateEnum.Success }))
  yield call(
    tracker.track,
    itemUploadStartEventExtra({
      tempUuid,
      screenName: item.isDraft ? Screen.ItemDraft : Screen.ItemEdit,
    }),
  )
}

export function* selectCatalog({ payload }: ReturnType<typeof statelessActions.selectCatalog>) {
  const {
    fieldErrors,
    setFieldErrors,
    removeFieldError,
    setAuthenticityModalContent,
    requestAuthenticityModal,
  } = payload

  const currentCatalog = yield* select(selectors.getCurrentCatalog)
  const catalog = yield* select(selectors.getCatalog, payload.catalogId)

  if (!catalog) return

  if (catalog.restrictedToStatusId) {
    yield put(actions.setStatusId({ id: null, itemStatus: payload.itemStatus, removeFieldError }))
  }

  if (!catalog.isIsbnInputShown) {
    yield put(
      actions.setIsbn({
        isbn: null,
        validity: IsbnValidity.Unvalidated,
        fieldErrors,
        setFieldErrors,
        removeFieldError,
      }),
    )
  }

  if (!catalog.isBrandSelectShown) {
    yield put(
      actions.setBrand({
        id: WITHOUT_BRAND_ID,
        title: null,
        isLuxury: false,
        itemStatus: payload.itemStatus,
        removeFieldError,
      }),
    )
  }

  if (currentCatalog) {
    if (currentCatalog.sizeGroupId !== catalog.sizeGroupId) {
      yield put(actions.setSizeId({ id: null, itemStatus: payload.itemStatus, removeFieldError }))
    }
  }

  yield put(
    actions.setCatalogId({
      id: payload.catalogId,
      itemStatus: payload.itemStatus,
      removeFieldError,
    }),
  )

  yield call(fetchCategoryAttributes, payload.catalogId, payload.itemStatus, removeFieldError)

  const brandIsLuxury = yield* select(selectors.getIsBrandLuxury)

  yield call(setAuthenticityModalContent, null)

  if (brandIsLuxury) {
    yield call(requestAuthenticityModal, { modalDataOnly: true, newCatalogId: payload.catalogId })
  }
}

export function* fetchSizeGroupsByCatalog() {
  const { catalogId } = yield* select(selectors.selectAttributes)

  if (!catalogId) return

  const isMultipleSizeGroupsEnabled = yield* select(selectors.getIsMultipleSizeGroupsABTestEnabled)
  const response = yield* call(api.getSizeGroupsByCatalog, catalogId, isMultipleSizeGroupsEnabled)

  if ('errors' in response) return

  const transformedDto = yield* call(transformSizeGroupDtos, response.size_groups)

  yield put(actions.setSizeGroups({ sizeGroups: transformedDto.sizeGroups }))
  yield put(actions.setSizes({ sizes: transformedDto.sizes }))
}

export function* fetchCatalogs() {
  const response = yield* call(api.getCatalogs)

  if ('errors' in response) return

  const catalogs = yield* call(transformers.transformCatalogs, response.catalogs)

  yield put(actions.setCatalogs({ catalogs }))
}

export function* fetchPriceSuggestions() {
  const { catalogId, statusId, brandId } = yield* select(selectors.selectAttributes)

  if (!brandId || !statusId || !catalogId) {
    yield put(actions.clearPriceSuggestions())

    return
  }

  const response = yield* call(api.getItemPriceSuggestions, { brandId, statusId, catalogId })

  if ('errors' in response) {
    yield put(actions.clearPriceSuggestions())

    return
  }

  const suggestions = transformers.transformPriceSuggestionsDto(response)

  yield put(actions.setPriceSuggestions({ suggestions }))
}

export function* fetchBookDetails({ payload }: ReturnType<typeof actions.setIsbn>) {
  if (payload.validity === IsbnValidity.Unvalidated) return

  if (payload.validity === IsbnValidity.Invalid || !payload.isbn) {
    yield call(payload.removeFieldError, FieldName.Isbn)

    return
  }

  yield put(actions.setIsbnPendingState())
  yield delay(REQUEST_DELAY)

  const response = yield* call(api.getBookDetails, { isbn: payload.isbn })

  if ('errors' in response) {
    yield call(payload.setFieldErrors, {
      byName: { ...payload.fieldErrors.byName, [FieldName.Isbn]: response.message },
      names: [...payload.fieldErrors.names, FieldName.Isbn],
    })

    return
  }

  const bookDetails = transformers.transformBookDetails(response)

  yield put(actions.setBookDetails({ bookDetails }))
}

export function* fetchPackageSizes() {
  const { catalogId } = yield* select(selectors.selectAttributes)
  if (!catalogId) return

  yield put(
    packageSizesActions.fetchCatalogPackageSizesRequest({
      catalogId,
    }),
  )
}

export function* handleNewItemIncompleteTaxAddressResponse({
  meta,
  payload: {
    user,
    tempUuid,
    itemStatus,
    screenName,
    additionalHeaders,
    trackListingToGoogleTagManager,
    trackListingToBraze,
    setFieldErrors,
    showEVSModal,
    showIVSModal,
    assignedPhotos,
  },
}: Pick<ReturnType<typeof statelessActions.submitItem>, 'meta' | 'payload'>) {
  yield put(actions.showMissingPostalCode())
  const action = yield* take(actions.hideMissingPostalCode)

  if (!action.payload?.resubmitItem) return

  yield put(
    statelessActions.submitItem({
      user,
      additionalHeaders,
      tempUuid,
      itemStatus,
      screenName,
      saveAsDraft: meta.saveAsDraft,
      isItemBumped: meta.isItemBumped,
      trackListingToGoogleTagManager,
      trackListingToBraze,
      setFieldErrors,
      showEVSModal,
      showIVSModal,
      assignedPhotos,
    }),
  )
}

export function* handleItemSubmitResponseCode({ responseCode, meta, payload }) {
  switch (responseCode) {
    case ResponseCode.IncompleteTaxAddress:
      yield* call(handleNewItemIncompleteTaxAddressResponse, { meta, payload })
      break
    case ResponseCode.PhotoMinimumCountRequired:
      yield put(actions.setIsLuxuryItemModalOpen({ isOpen: true }))
      break
    default:
      break
  }
}

export function* submitItem({ meta, payload }: ReturnType<typeof statelessActions.submitItem>) {
  const {
    user,
    tempUuid,
    itemStatus,
    screenName,
    additionalHeaders,
    trackListingToGoogleTagManager,
    trackListingToBraze,
    setFieldErrors,
    showEVSModal,
    showIVSModal,
    assignedPhotos,
  } = payload

  yield put(actions.setIsFormDisabled({ isFormDisabled: true }))

  let response: SubmitItemResp | SubmitDraftResp | ResponseError

  const isNewVerificationServiceEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_verification_service_web'),
  )
  const isNewCreateAndEditItemEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_create_and_edit_item_new_web'),
  )
  const isNewCreateAndEditDraftEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_create_and_edit_draft_new_web'),
  )
  const isNewItemCompletionEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_item_completion_new_web'),
  )
  const itemAttributes = yield* select(selectors.selectAttributes)
  const dynamicAttributes = yield* select(selectors.getSelectedDynamicAttributes)
  const currency = yield* select(selectors.getCurrency)
  const itemDto = yield* call(
    transformers.transformItemAttributesToItemUploadDto,
    itemAttributes,
    assignedPhotos,
    dynamicAttributes,
    currency,
    tempUuid,
  )

  const { isItemBumped, saveAsDraft } = meta

  if (saveAsDraft) {
    if (itemDto.id) {
      const updateItemDraftArgs: UpdateItemDraftArgs = {
        draft: itemDto,
        feedbackId: itemAttributes.feedbackId,
        additionalHeaders,
      }

      if (isNewCreateAndEditDraftEnabled) {
        response = yield* call(api.updateItemDraft, updateItemDraftArgs)
      } else {
        response = yield* call(api.updateItemDraftDeprecated, updateItemDraftArgs)
      }
    } else {
      const createItemDraftArgs: CreateItemDraftArgs = {
        draft: itemDto,
        feedbackId: itemAttributes.feedbackId,
        additionalHeaders,
      }

      if (isNewCreateAndEditDraftEnabled) {
        response = yield* call(api.createItemDraft, createItemDraftArgs)
      } else {
        response = yield* call(api.createItemDraftDeprecated, createItemDraftArgs)
      }
    }
  } else if (itemStatus === ItemStatus.DraftEdit && itemDto.id) {
    const completeItemArgs: CompleteItemArgs = {
      draft: itemDto,
      feedbackId: itemAttributes.feedbackId,
      additionalHeaders,
    }

    if (isNewItemCompletionEnabled) {
      response = yield* call(api.completeItem, { ...completeItemArgs, isItemBumped })
    } else {
      response = yield* call(api.completeItemDeprecated, completeItemArgs)
    }

    if (
      (isItemBumped && !isNewItemCompletionEnabled) ||
      ('item' in response &&
        response.after_upload_actions?.includes(ItemAfterUploadActions.ShowBumpsCheckout))
    ) {
      setPromotionsAfterItemUploadToStorage(response, PromotionStorageKeys.ShowBumped)
    } else {
      setPromotionsAfterItemUploadToStorage(response, PromotionStorageKeys.ShowUploadAnotherItemTip)
    }
  } else if (itemDto.id) {
    const updateItemArgs: UpdateItemArgs = {
      item: itemDto,
      feedbackId: itemAttributes.feedbackId,
      additionalHeaders,
    }

    if (isNewCreateAndEditItemEnabled) {
      response = yield* call(api.updateItem, { ...updateItemArgs, isItemBumped })
    } else {
      response = yield* call(api.updateItemDeprecated, updateItemArgs)
    }

    if (
      (isItemBumped && !isNewCreateAndEditItemEnabled) ||
      ('item' in response &&
        response.after_upload_actions?.includes(ItemAfterUploadActions.ShowBumpsCheckout))
    ) {
      setPromotionsAfterItemUploadToStorage(response, PromotionStorageKeys.ShowBumped)
    }
  } else {
    const createItemArgs: CreateItemArgs = {
      item: itemDto,
      feedbackId: itemAttributes.feedbackId,
      additionalHeaders,
    }

    if (isNewCreateAndEditItemEnabled) {
      response = yield* call(api.createItem, { ...createItemArgs, isItemBumped })
    } else {
      response = yield* call(api.createItemDeprecated, createItemArgs)
    }

    if (
      (isItemBumped && !isNewCreateAndEditItemEnabled) ||
      ('item' in response &&
        response.after_upload_actions?.includes(ItemAfterUploadActions.ShowBumpsCheckout))
    ) {
      setPromotionsAfterItemUploadToStorage(response, PromotionStorageKeys.ShowBumped)
    } else {
      setPromotionsAfterItemUploadToStorage(response, PromotionStorageKeys.ShowUploadAnotherItemTip)
    }
  }

  if (!itemAttributes.feedbackId) {
    setPromotionsAfterItemUploadToStorage(response, PromotionStorageKeys.ShowFeedback)
  }

  if (!response) {
    yield put(actions.setIsFormDisabled({ isFormDisabled: false }))

    return
  }

  if ('errors' in response) {
    yield call(setFieldErrors, getFieldErrors(response.errors))
    // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
    yield* call(scrollToElementById, response.errors[0]!?.field)
    yield put(actions.setIsFormDisabled({ isFormDisabled: false }))

    tracker.track(
      itemUploadSubmitFail({
        reason: ItemUploadFailReason.ValidationError,
        validationErrors: response.errors.map(error => error.value),
        screen: screenName,
        tempUuid,
      }),
    )

    yield call(handleItemSubmitResponseCode, {
      responseCode: response.code,
      meta,
      payload,
    })

    return
  }

  let redirectUrl = ROOT_URL
  let itemId = itemDto.id

  if ('item' in response) {
    const { item } = response
    const isItemEdit = itemStatus === ItemStatus.Edit
    const isNewListing = !saveAsDraft && !isItemEdit

    if (isNewListing) {
      const gtmTrackableItem: GtmTrackableItemDto = {
        id: item.id,
        catalog_id: itemAttributes.catalogId,
        currency,
        price: { amount: itemAttributes.price?.toString() || '', currency_code: currency },
        title: itemAttributes.title,
        user: { email: user.email, external_id: user.externalId, id: user.id },
      }

      yield call(trackListingToGoogleTagManager, gtmTrackableItem)
      yield call(trackListingToBraze, { itemId: item.id, userExternalId: user.externalId })
    }

    redirectUrl = MEMBER_PROFILE_URL(user.id)
    itemId = item.id
  } else if ('draft' in response) {
    redirectUrl = MEMBER_PROFILE_URL(user.id)
    itemId = response.draft.id
  }

  tracker.track(
    itemUploadSubmitSuccess({
      itemId: itemId || undefined,
      tempUuid,
      screen: screenName,
    }),
  )

  if (isNewVerificationServiceEnabled) {
    if (response.after_upload_actions?.includes(ItemAfterUploadActions.ShowIVSModal)) {
      if (itemId) setItemIdToStorage(itemId)
      yield* call(showIVSModal)

      return
    }

    if (response.after_upload_actions?.includes(ItemAfterUploadActions.ShowEVSModal)) {
      if (itemId) setItemIdToStorage(itemId)
      yield* call(showEVSModal)

      return
    }
  } else if (
    response.after_upload_actions?.includes(ItemAfterUploadActions.ShowOfflineVerificationModal)
  ) {
    if (itemId) setItemIdToStorage(itemId)
    yield put(actions.setIsOfflineVerificationModalOpen({ isOpen: true }))

    return
  }

  if (itemId) setItemIdToStorage(itemId)

  yield* call(navigateToPage, redirectUrl)
}

export function* fetchItemSuggestions({
  payload,
}: ReturnType<typeof statelessActions.fetchItemSuggestionsRequest>) {
  const isKillswitchEnabled = yield* select(
    getIsFeatureSwitchEnabled('killswitch_item_upload_suggestions_web'),
  )

  if (isKillswitchEnabled) return

  yield delay(REQUEST_DELAY)

  const { title, description, catalogId } = yield* select(selectors.selectAttributes)
  const isMultipleSizeGroupsEnabled = yield* select(selectors.getIsMultipleSizeGroupsABTestEnabled)

  const args = {
    title,
    description,
    catalogId,
    photoIds: payload.photoIds,
    isMultipleSizeGroupsEnabled,
  }

  const response = yield* call(api.getItemSuggestions, args)

  if ('errors' in response) return

  const { colors, sizes, brands, catalogs } = yield* call(
    transformers.transformItemSuggestions,
    response,
  )

  yield put(actions.setSuggestions({ colors, sizes, brands, catalogs }))
}

export function* fetchItemOfflineVerificationEligibility() {
  const isNewVerificationServiceEnabled = yield* select(
    getIsFeatureSwitchEnabled('item_upload_verification_service_web'),
  )

  if (isNewVerificationServiceEnabled) return

  yield delay(REQUEST_DELAY)

  const { catalogId, price, brandId } = yield* select(selectors.selectAttributes)

  if (!brandId || !price || !catalogId) {
    yield put(actions.setIsOfflineVerificationEligible({ eligible: false }))

    return
  }

  const response = yield* call(api.getItemOfflineVerificationEligibility, {
    brandId,
    price,
    catalogId,
  })

  if ('errors' in response) {
    yield put(actions.setIsOfflineVerificationEligible({ eligible: false }))

    return
  }

  yield put(actions.setIsOfflineVerificationEligible({ eligible: response.eligible }))
}

export default function* saga() {
  yield takeLatest(statelessActions.fetchItemSuggestionsRequest, fetchItemSuggestions)

  yield takeLatest(statelessActions.fetchItemRequest, fetchItem)

  yield takeEvery(statelessActions.selectCatalog, selectCatalog)

  yield takeEvery(statelessActions.fetchSizeGroupsByCatalog, fetchSizeGroupsByCatalog)

  yield takeEvery(statelessActions.submitItem, submitItem)

  yield takeLatest(
    [
      statelessActions.fetchPriceSuggestions,
      actions.setBrand,
      actions.setStatusId,
      actions.setCatalogId,
    ],
    fetchPriceSuggestions,
  )

  yield takeLatest(actions.setIsbn, fetchBookDetails)

  yield takeLatest(
    [actions.setCatalogId, statelessActions.fetchPackageSizesRequest],
    fetchPackageSizes,
  )

  yield takeLatest(statelessActions.fetchCatalogs, fetchCatalogs)

  yield takeLatest(
    [
      statelessActions.fetchItemOfflineVerificationEligibility,
      actions.setBrand,
      actions.setPrice,
      actions.setCatalogId,
    ],
    fetchItemOfflineVerificationEligibility,
  )
}
